import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, getSavedCreds } from '../../contexts/AuthContext';
import { IdentifyEventUser, GuestSignIn, sendOtp, verifyOtp } from '../../api/api';
import { startSessionLog } from '../../utils/session';
import { verifyDevice, loadOrganisers, needsPasswordChange, humanReason, thisDeviceId } from '../../utils/authFlow';
import { isDemoMobile } from '../../utils/demo';
import { tenDigits } from '../../utils/helpers';
import { GUEST_EVENT_ID, setGuestSession, clearGuestSession } from '../../utils/guestSession';

// All Login logic — shared by Login.desktop / Login.mobile (the APK uses the mobile one).
//
// Mobile number FIRST, then the API says who it is (IdentifyEventUser, for the
// guest event - utils/guestSession):
//   step 'mobile'   → type the number, Continue
//   step 'password' → staff (admin / member / vendor / demo): the usual password
//                     sign-in with device binding (verify below)
//   step 'otp'      → a known GUEST: WhatsApp OTP → GuestSignIn → /guest
//   unknown number  → refused, stays on 'mobile'
// If the identify call itself fails (old API, network) we fall back to the
// password step, so staff are never locked out by it.
export function useLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { state } = useLocation();
  const [step, setStep] = useState('mobile');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtpRaw] = useState('');
  const [reused, setReused] = useState(null);   // Date_Time of a still-valid earlier OTP
  const [guestName, setGuestName] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  // A message handed over by DeviceGuard / ChangePassword / the guest screen.
  const [err, setErr] = useState(state?.msg || '');
  // Why the last attempt failed ('bad_password' | …) → the view offers "Reset it with OTP".
  const [errReason, setErrReason] = useState('');
  // Show a "signing in" splash while we re-verify saved credentials on boot.
  const [autoChecking, setAutoChecking] = useState(() => !!getSavedCreds());

  // Staff: verify mobile + password + THIS device against the API and route.
  // `keep` → persist credentials for next launch. Returns true if it handled routing.
  //   ok                → sign in (binds an unbound account to this device)
  //   device_mismatch   → /otp to move the account here (or Super Admin reset)
  //   password == 1234  → sign in, then compulsory /change-password
  // The demo account (utils/demo.js) skips binding and OTP on the server side.
  const verify = useCallback(async (m, p, keep) => {
    const res = await verifyDevice(m, p, { bind: true });
    if (!res?.ok) {
      if (res?.reason === 'device_mismatch') {
        nav('/otp', { state: { mobile: m, password: p, pendingUser: res.user, remember: keep, boundModel: res.boundModel, boundDate: res.boundDate } });
        return true;
      }
      setErr(humanReason(res?.reason)); setErrReason(res?.reason || ''); return false;
    }
    const user = res.user;
    const list = await loadOrganisers(user);
    if (list.length === 0) { setErr('No organiser is assigned to this user yet'); return false; }
    const mustChange = needsPasswordChange(m, p);
    clearGuestSession(); // staff sign-in replaces any guest session on this device
    login(user, list[0], { mobile: m, password: p }, keep, mustChange);
    startSessionLog(user, await thisDeviceId()); // open the activity-log session (best-effort)
    nav(mustChange ? '/change-password' : '/app/dashboard', { replace: true });
    return true;
  }, [login, nav]);

  // Auto sign-in on app start using saved (staff) credentials.
  useEffect(() => {
    const creds = getSavedCreds();
    if (!creds?.mobile || (!creds.password && !isDemoMobile(creds.mobile))) { setAutoChecking(false); return; }
    setMobile(creds.mobile);
    setPassword(creds.password);
    setStep('password');
    let cancelled = false;
    (async () => {
      try {
        await verify(creds.mobile, creds.password, true);
      } catch {
        if (!cancelled) setErr('Auto sign-in failed. Check your connection and sign in again.');
      } finally {
        if (!cancelled) setAutoChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [verify]);

  const requestOtp = async () => {
    const r = await sendOtp({ MobileNumber: mobile, ServiceBy: 'Mehfil by Smartify' });
    setReused(r?.reused ? r.Date_Time : null);
  };

  // Step 1: who is this number?
  const continueWithMobile = async (e) => {
    e?.preventDefault();
    setErr(''); setErrReason('');
    if (!/^\d{10}$/.test(mobile)) { setErr('Enter a valid 10-digit mobile number'); return; }
    if (isDemoMobile(mobile)) { setStep('password'); return; }
    setBusy(true);
    let kind = null;
    try {
      const r = await IdentifyEventUser({ MobileNumber: mobile, EventId: GUEST_EVENT_ID });
      kind = r?.kind || null;
      setGuestName(r?.name || '');
    } catch {
      kind = null; // API not reachable / not updated yet → the old password sign-in
    }
    try {
      if (kind === 'guest') {
        await requestOtp();
        setOtpRaw('');
        setStep('otp');
      } else if (kind === 'not_found') {
        setErr('This number is not on our list. Please contact the family who invited you.');
      } else if (kind === 'inactive') {
        setErr(humanReason('inactive'));
      } else {
        setStep('password'); // 'staff', or the check could not run
      }
    } catch {
      setErr('Could not send the OTP. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  // Step 2 (staff): password.
  const submit = async (e) => {
    e?.preventDefault();
    setErr(''); setErrReason('');
    if (!/^\d{10}$/.test(mobile)) { setErr('Enter a valid 10-digit mobile number'); return; }
    // A test account (Play Store review / demo) signs in on the number alone —
    // the API skips the password check, the OTP and the device binding for it.
    if (!password && !isDemoMobile(mobile)) { setErr('Enter your password'); return; }
    setBusy(true);
    try {
      await verify(mobile, password, remember);
    } catch {
      setErr('Login failed. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  // Step 2 (guest): OTP → GuestSignIn (the API re-checks the verified OTP and
  // creates the guest's user row if needed) → guest screen.
  const verifyGuest = async (e) => {
    e?.preventDefault();
    setErr('');
    if (!/^\d{4}$/.test(otp)) { setErr('Enter the 4-digit OTP'); return; }
    setBusy(true);
    try {
      const v = await verifyOtp({ MobileNumber: mobile, OTP: otp });
      if (!v?.verified) { setErr(v?.reason || 'Invalid OTP'); return; }
      const r = await GuestSignIn({ MobileNumber: mobile, EventId: GUEST_EVENT_ID });
      if (!r?.ok) {
        if (r?.reason === 'staff') { setStep('password'); setErr('Please sign in with your password.'); return; }
        setErr({
          otp_required: 'The OTP could not be confirmed. Please try again.',
          not_found: 'This number is not on our list. Please contact the family who invited you.',
          inactive: humanReason('inactive'),
        }[r?.reason] || 'Could not sign you in. Please try again.');
        return;
      }
      setGuestSession({ user: r.user, event: r.event, side: r.side || 'Common', mobile });
      nav('/guest', { replace: true });
    } catch {
      setErr('Verification failed. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const resendOtp = async () => {
    setErr('');
    try { await requestOtp(); } catch { setErr('Could not resend the OTP'); }
  };

  // Back to step 1 with a different number.
  const changeNumber = () => {
    setStep('mobile'); setPassword(''); setOtpRaw(''); setErr(''); setErrReason(''); setReused(null); setGuestName('');
  };

  // Last 10 digits: autofill / paste gives "+91 98220 18644" or "098220 18644".
  const setMobileDigits = (v) => setMobile(tenDigits(v));
  const setOtp = (v) => setOtpRaw(String(v || '').replace(/\D/g, '').slice(0, 4));
  // Existing account, password forgotten -> WhatsApp OTP -> new password -> signed in.
  const forgotPassword = () => nav('/forgot-password', { state: { mobile } });

  return {
    step, mobile, setMobile: setMobileDigits, password, setPassword, otp, setOtp, reused, guestName,
    remember, setRemember, busy, err, errReason, autoChecking,
    continueWithMobile, submit, verifyGuest, resendOtp, changeNumber, forgotPassword,
  };
}
